import type {
  DirectiveResult,
  SYSActions,
  SYSDialog,
  SYSFeature,
  SYSForm,
  SYSState,
  UnsafeHTMLDirective,
} from "../.."
import { html, ovl } from "../.."
import { TemplateResult } from "../../"

import { DialogResult } from "../actions"
import { FormInit, FormReset, type FormInitOptions } from "../forms/formapi"
import type {
  SYSFormField,
  ConnectedFormConfig_Type,
} from "../forms/sysformelement"
import type { SYSFormState } from "../forms/fieldapi"
import { _DialogCancelType, _DialogOkType, type ResultType } from "./dialog"
import { FormBodyRenderer } from "../table/helpers"
import type {
  DialogsState,
  DialogType,
  FormHandlingType,
} from "./sysdialogbase"

export type EditorStart_Type = {
  formState: SYSFormState
  fieldKey: string
}

export type EditorStartContext_Type = {
  state: SYSState
  actions: SYSActions
}

export type DialogFormResult = {
  result: number
  formState: SYSFormState
}

type DialogOkCancelType = {
  defaultButton?: ResultType
  type?: DialogType
  customClass?: string
  buttonText_01?: _DialogOkType
  buttonText_02?: _DialogCancelType
  buttonText_03?: string
  instance?: any
  title?: TemplateResult | string | DirectiveResult
  connectedForm?: { formType: string; formId: string }
  connectedFormDefinition?: ConnectedFormConfig_Type
}

export async function DialogOkCancel(
  text: TemplateResult | string | DirectiveResult,
  options: DialogOkCancelType & {
    connectedFormDefinition: ConnectedFormConfig_Type & { feature: SYSFeature }
  }
): Promise<DialogFormResult>
export async function DialogOkCancel(
  text: TemplateResult | string | DirectiveResult,
  options?: DialogOkCancelType
): Promise<number>
export async function DialogOkCancel(
  text: TemplateResult | string | DirectiveResult,
  options?: DialogOkCancelType
): Promise<number | DialogFormResult> {
  let instance = options?.instance ? options.instance : "SYSModal"
  if (DialogIsOpen("SYSModal", instance)) {
    // make sure only 1 dialog of same type is opened
    return
  }

  let connectedForm = options?.connectedForm
  let formState: SYSFormState
  let config = options?.connectedFormDefinition
  if (config) {
    let res = await FormInit(<SYSForm>"SYSEdit", config.fields, {
      feature: config.feature,
      subFeature: config.options?.subFeature,
      formId: config.options?.formId || instance,
      fillSpaceIfNotVisible:
        config.options?.fillSpaceIfNotVisible === undefined
          ? false
          : config.options.fillSpaceIfNotVisible,
      forceOverwrite: config.options?.forceOverwrite || false,
    })
    formState = res.formState
    if (
      config.options?.validateOk === undefined ||
      config.options?.validateOk
    ) {
      connectedForm = {
        formType: formState.formType,
        formId: formState.formId,
      }
    }

    let formBody = FormBodyRenderer(
      formState,
      {
        feature: formState.feature,
        columns: formState.fields,
        options: { rendering: { groups: {} } },
      },
      undefined,
      undefined,
      undefined
    )

    text = html`${text}
      <div class="ovl-dialog-connected-form">${formBody}</div>`
  }

  ovl.getActions().ovl.dialog._OkCancelDialog({
    text,
    default: options?.defaultButton || 1,
    type: options?.type || "confirmation",
    customClass: options?.customClass,
    buttonText_01: options?.buttonText_01,
    buttonText_02: options?.buttonText_02,
    buttonText_03: options?.buttonText_03,
    instance,
    title: options?.title,
    connectedForm: connectedForm,
  })

  let result = (await DialogResult(instance)) as any
  if (formState) {
    return { result, formState }
  }
  return result
}

type DialogOkType = {
  type?: DialogType
  customClass?: string
  buttonText_01?: _DialogOkType
  instance?: any
  title?: TemplateResult | string | UnsafeHTMLDirective | DirectiveResult
  connectedForm?: { formType: string; formId: string }
  connectedFormDefinition?: ConnectedFormConfig_Type
}

export async function DialogOk(
  text: TemplateResult | string | UnsafeHTMLDirective | DirectiveResult,
  options: DialogOkType & {
    connectedFormDefinition: ConnectedFormConfig_Type & { feature: SYSFeature }
  }
): Promise<DialogFormResult>
export async function DialogOk(
  text: TemplateResult | string | UnsafeHTMLDirective | DirectiveResult,
  options?: DialogOkType
): Promise<number>
export async function DialogOk(
  text: string | TemplateResult | DirectiveResult,
  options?: DialogOkType
): Promise<number | DialogFormResult> {
  let instance = options?.instance ? options?.instance : "SYSModal"
  if (DialogIsOpen("SYSModal", instance)) {
    // make sure only 1 dialog of same type is opened
    return
  }

  let connectedForm = options?.connectedForm
  let formState: SYSFormState
  let config = options?.connectedFormDefinition
  if (config) {
    let res = await FormInit(<SYSForm>"SYSEdit", config.fields, {
      feature: config.feature,
      subFeature: config.options?.subFeature,
      formId: config.options?.formId || instance,
      fillSpaceIfNotVisible:
        config.options?.fillSpaceIfNotVisible === undefined
          ? false
          : config.options.fillSpaceIfNotVisible,
      forceOverwrite: config.options?.forceOverwrite || false,
    })
    formState = res.formState
    if (
      config.options?.validateOk === undefined ||
      config.options?.validateOk
    ) {
      connectedForm = {
        formType: formState.formType,
        formId: formState.formId,
      }
    }

    let formBody = FormBodyRenderer(
      formState,
      {
        feature: formState.feature,
        columns: formState.fields,
        options: { rendering: { groups: {} } },
      },
      undefined,
      undefined,
      undefined
    )

    text = html`${text}
      <div class="ovl-dialog-connected-form">${formBody}</div>`
  }

  ovl.getActions().ovl.dialog._OkDialog({
    text,
    type: options?.type || "information",
    customClass: options?.customClass,
    buttonText_01: options?.buttonText_01,
    instance,
    title: options?.title,
    connectedForm: connectedForm,
  })

  let result = (await DialogResult(instance)) as any
  if (formState) {
    return { result, formState }
  }
  return result
}

export const IsDialogOpen = (dialogType: SYSDialog, instance?: any) => {
  let dlgMainState = ovl.getState().ovl.dialogs[dialogType]
  if (!instance) {
    return Object.keys(dlgMainState).some(
      (instance) =>
        dlgMainState[dialogType][instance].visible &&
        !dlgMainState[dialogType][instance].closing
    )
  }
  return (
    dlgMainState[dialogType][instance].visible &&
    !dlgMainState[dialogType][instance].closing
  )
}

export type OpenDialogOptionsType = {
  instance?: any
  // focus?: {
  //   persistFocus?: boolean
  //   forceElementIdToFocusAfterOpen?: string
  //   forceElementIdToFocusAfterClose?: string
  // }
  formsHandling?: FormHandlingType[]
  data?: any
}

export type CloseDialogOptionsType = {
  instance?: any
  forceElementIdToFocusAfterClose?: string
  resolveValue?: any
}

// export const SetResolveValueDialog = (dialogType: SYSDialog, value: any) => {
//   ovl.getState().ovl.dialogs[dialogType].resolveValue = value
// }

export const DialogState = (
  dialog?: SYSDialog,
  instance?: any
): DialogsState => {
  if (!dialog) {
    dialog = DialogCurrent()
  }
  if (!instance) {
    instance = dialog
  }
  return ovl.getState().ovl.dialogs[dialog]?.instance?.[instance]
}

export const DialogModal = async (
  dialogType: SYSDialog,
  options?: OpenDialogOptionsType
): Promise<any> => {
  ovl.getState().ovl.libState.dialog.result = undefined
  return await ovl.getActions().ovl.dialog._DialogOpen({
    dialogType,
    // forceElementIdToFocusAfterClose:
    //   options?.focus?.forceElementIdToFocusAfterClose,
    // forceElementIdToFocusAfterOpen:
    //   options?.focus?.forceElementIdToFocusAfterOpen,

    modal: true,
    formsHandling: options?.formsHandling,
    data: options?.data,
    instance: options?.instance,
  })
}
export const DialogCurrent = (
  dialogToIgnore: SYSDialog = undefined
): SYSDialog => {
  let res = DialogCurrentState()
    .filter((f) => f.dialog !== dialogToIgnore)
    .map((m) => m.dialog)
  if (res.length > 0) {
    return <SYSDialog>res[0]
  } else {
    return undefined
  }
}

/* returns DialogStates of currently open Dialog in order of zIndex Desc */
export const DialogCurrentState = (): DialogsState[] => {
  let dialogsState = ovl.getState().ovl.dialogs
  let res: DialogsState[] = []
  Object.keys(dialogsState).forEach((f) =>
    Object.keys(dialogsState[f].instance).forEach((instance) => {
      if (
        dialogsState[f].instance[instance].visible &&
        !dialogsState[f].instance[instance].closing
      ) {
        res.push(dialogsState[f].instance[instance])
      }
    })
  )
  res.sort((a, b) => b.zIndex - a.zIndex)
  return res
}

export const DialogIsOpen = (dialog: SYSDialog, instance?: string): boolean => {
  if (!instance) {
    instance = dialog
  }
  let dialogsState = ovl.getState().ovl.dialogs
  if (dialogsState[dialog].instance[instance] === undefined) {
    return false
  }
  return (
    dialogsState[dialog].instance[instance].visible &&
    !dialogsState[dialog].instance[instance].closing
  )
}

export const DialogOpen = async (
  dialogType: SYSDialog,
  options?: OpenDialogOptionsType
) => {
  await ovl.getActions().ovl.dialog._DialogOpen({
    dialogType,
    formsHandling: options?.formsHandling,
    instance: options?.instance,
  })
}

export type ResolveDialogOptionsType = {
  instance?: any
}

// needs more thinking (it works like this but not sure of the implications...)
// export const DialogResolve = async (
//   dialogType: SYSDialog,
//   resolveValue: any,
//   options?: ResolveDialogOptionsType
// ) => {
//   let instance = options?.instance
//   if (!instance) {
//     instance = dialogType
//   }
//   let state = ovl.getState()
//   let dlgState = state.ovl.dialogs[dialogType].instance[instance]
//   if (dlgState.modal && dlgState.resolver) {
//     dlgState.resolver(resolveValue)
//   }
// }
export const DialogClose = async (
  dialogType: SYSDialog,
  options?: CloseDialogOptionsType
) => {
  await ovl.getActions().ovl.dialog._DialogClose({
    dialog: dialogType,
    forceElementIdToFocusAfterClose: options?.forceElementIdToFocusAfterClose,
    resolveValue: options?.resolveValue,
    instance: options?.instance,
  })
}

export type DialogSetLockOptionsType = {
  instance?: any
}

export const DialogSetLock = (
  dialogType: SYSDialog,
  lock: boolean,
  options?: DialogSetLockOptionsType
) => {
  ovl.getActions().ovl.dialog._DialogSetLock({
    dialog: dialogType,
    locked: lock,
    instance: options?.instance,
  })
}
export type { _DialogHolderParams as DialogHolderParams } from "./sysdialogholder"
